// build-2026-05-26-rbac
import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import axios from 'axios';
import Layout            from './components/Layout';
import './App.css';
import './styles/portal-theme.css';

/* ── Public pages: kept eager so the landing/login routes paint immediately ── */
import LoginPage      from './pages/LoginPage';
import LandingPage    from './pages/LandingPage';

/* ── Authenticated app pages: code-split so an anonymous visitor to "/"
   never downloads recharts, xlsx, date-fns, etc. — these only load once
   someone actually logs in and navigates to the route. ───────────────── */
const Dashboard          = lazy(() => import('./pages/Dashboard'));
const ClientsPage        = lazy(() => import('./pages/ClientsPage'));
const ClientServicesPage = lazy(() => import('./pages/ClientServicesPage'));
const LeadsPage          = lazy(() => import('./pages/LeadsPage'));
const SettingsPage       = lazy(() => import('./pages/SettingsPage'));
const RemindersPage      = lazy(() => import('./pages/RemindersPage'));
const CalendarPage       = lazy(() => import('./pages/CalendarPage'));
const TimesheetPage      = lazy(() => import('./pages/TimesheetPage'));
const ClientPortalPage   = lazy(() => import('./pages/ClientPortalPage'));

const RouteFallback = () => (
  <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div className="animate-spin" style={{ width: 28, height: 28, borderRadius: '9999px',
           border: '3px solid #E5E7EB', borderTopColor: '#111827' }} />
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Session-expiry handling: on any 401 from an authenticated request, clear
// the stale session and bounce to /login with a clear message. Excludes
// /auth/ endpoints so a failed login/signup attempt (which also 401s) shows
// its own "invalid credentials" toast instead of silently redirecting —
// that's what broke this the first time it was tried (commit 34b8f3d).
let sessionExpiredNoticeShown = false;
axios.interceptors.response.use(
  response => response,
  error => {
    const isAuthEndpoint = (error.config?.url || '').includes('/auth/');
    if (error.response?.status === 401 && !isAuthEndpoint && localStorage.getItem('token')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('organization');
      if (!sessionExpiredNoticeShown) {
        sessionExpiredNoticeShown = true;
        toast.error('Your session has expired — please log in again.');
      }
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser]                       = useState(null);
  const [organization, setOrganization]       = useState(null);
  const [loading, setLoading]                 = useState(true);
  const [unreadNotesCount, setUnreadNotesCount] = useState(0);

  const fetchUnreadCount = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API}/service-notes/unread-count`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setUnreadNotesCount(r.data?.count || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    try {
      const token    = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      const orgData  = localStorage.getItem('organization');
      if (token && userData) {
        const parsedUser = JSON.parse(userData);          // may throw if JSON is malformed
        const parsedOrg  = orgData ? JSON.parse(orgData) : null;
        setIsAuthenticated(true);
        setUser(parsedUser);
        if (parsedOrg) setOrganization(parsedOrg);
      }
    } catch (e) {
      // Corrupted localStorage — clear stale/partial state and force re-login
      console.warn('[App] localStorage parse failed, clearing session:', e.message);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('organization');
    } finally {
      // Always unblock the loading screen regardless of any error above
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchUnreadCount]);

  const handleLogin = (token, userData, orgData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('organization', JSON.stringify(orgData));
    setIsAuthenticated(true);
    setUser(userData);
    setOrganization(orgData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('organization');
    setIsAuthenticated(false);
    setUser(null);
    setOrganization(null);
  };

  if (loading) return <div style={{ minHeight: '100vh', background: '#fff' }} />;

  const wrap = (Page) =>
    isAuthenticated
      ? <Layout user={user} organization={organization} onLogout={handleLogout}
                unreadNotesCount={unreadNotesCount} onNotesRead={fetchUnreadCount}>
          <Page onNotesRead={fetchUnreadCount} />
        </Layout>
      : <Navigate to="/" replace />;

  const wrapLeads = (Page) => {
    if (!isAuthenticated) return <Navigate to="/" replace />;
    if (user?.role === 'consultant') return <Navigate to="/dashboard" replace />;
    return (
      <Layout user={user} organization={organization} onLogout={handleLogout}
              unreadNotesCount={unreadNotesCount} onNotesRead={fetchUnreadCount}>
        <Page onNotesRead={fetchUnreadCount} />
      </Layout>
    );
  };

  return (
    <BrowserRouter>
      <Toaster position="top-right" />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* ── Public ─────────────────────────────────────────── */}
          <Route path="/"      element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
          <Route path="/home"  element={<LandingPage />} />
          <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage onLogin={handleLogin} />} />

          {/* ── Protected ──────────────────────────────────────── */}
          <Route path="/dashboard"       element={wrap(Dashboard)} />
          <Route path="/clients"            element={wrap(ClientsPage)} />
          <Route path="/clients/:clientId"  element={wrap(ClientPortalPage)} />
          <Route path="/client-services"    element={wrap(ClientServicesPage)} />
          <Route path="/settings"        element={wrap(SettingsPage)} />
          <Route path="/leads"           element={wrapLeads(LeadsPage)} />
          <Route path="/reminders"       element={wrap(RemindersPage)} />
          <Route path="/calendar"        element={wrap(CalendarPage)} />
          <Route path="/timesheet"       element={wrap(TimesheetPage)} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
